import { useState } from "react";
import { Link } from "react-router-dom";
import { requisitionApi } from "../../api";
import { ProcError, ProcLoading, ProcPagination, ProcTabs } from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useAsyncData, usePageState } from "../../hooks";
import type { RequisitionStatus } from "../../types";

const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Draft", value: "DRAFT" },
  { label: "Submitted", value: "SUBMITTED" },
  { label: "Dept Head", value: "DEPT_HEAD_APPROVED" },
  { label: "Finance", value: "FINANCE_APPROVED" },
  { label: "Procurement", value: "PROCUREMENT_REVIEWED" },
  { label: "MD Approved", value: "MD_APPROVED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Cancelled", value: "CANCELLED" },
];

export default function RequisitionListPage() {
  const [status, setStatus] = useState<RequisitionStatus | "">("");
  const { page, setPage, state } = usePageState();

  const { data, loading, error } = useAsyncData(
    () => requisitionApi.list({ status: status || undefined, page: state.page, size: state.size }),
    [status, state.page, state.size],
  );

  function handleTabChange(value: string) {
    setStatus(value as RequisitionStatus | "");
    setPage(0);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Purchase Requisitions</h1>
        </div>
        <Link
          to="/procurement/requisitions/new"
          className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
        >
          New Requisition
        </Link>
      </div>

      <ProcTabs tabs={STATUS_TABS} active={status} onChange={handleTabChange} />

      {loading && <ProcLoading />}
      {error && <ProcError message={error} />}

      {!loading && !error && data && (
        <>
          <div className="rounded-2xl border bg-white shadow-sm overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Serial No</th>
                  <th className="px-4 py-3">Department</th>
                  <th className="px-4 py-3">Purpose</th>
                  <th className="px-4 py-3">Budget Line</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Next Approver</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.content.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                      No requisitions found.
                    </td>
                  </tr>
                )}
                {data.content.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-semibold text-slate-900">{r.serialNo}</td>
                    <td className="px-4 py-3 text-slate-600">{r.departmentName}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{r.purpose}</td>
                    <td className="px-4 py-3 text-slate-500">{r.budgetLine || "—"}</td>
                    <td className="px-4 py-3">
                      <ProcStatusBadge value={r.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {r.currentApproverUsername
                        ? `${r.currentStepName ?? r.nextApproverRole ?? ""} — ${r.currentApproverUsername}`
                        : r.nextApproverRole ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {r.date ? new Date(r.date).toLocaleDateString("en-KE") : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/procurement/requisitions/${r.id}`}
                        className="font-semibold text-emerald-700 hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ProcPagination
            page={data.number}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            onPage={setPage}
          />
        </>
      )}
    </div>
  );
}
